import {
  getDarkerColor,
  getLighterColor,
  getReadableTextColor,
  getContrastSafeBrandColor,
} from "./colorUtils";

/**
 * Apply CSS custom properties from tenant settings.
 * Called on settings load to make theme values available as CSS variables.
 *
 * Exposes derived tokens alongside the raw brand color so components
 * can use them directly instead of computing color-mix strings inline:
 *
 *   --color-primary          raw value as stored on settings
 *   --color-primary-hover    10% darker (for hover states)
 *   --color-primary-active   18% darker (for pressed / active)
 *   --color-primary-soft     85% lighter (for tinted backgrounds)
 *   --color-primary-text     #000 or #fff — whichever passes WCAG
 *                            against the brand color
 *   --color-primary-safe-light  contrast-safe brand variant for
 *                              light-mode UI (passes 3.0 against #fff)
 *   --color-primary-safe-dark   ditto for dark-mode UI (against #0f172a)
 *
 *   --color-secondary-*      same family for secondary_color
 *
 * The "safe" variants exist so a tenant who picks navy as their
 * primary doesn't end up with invisible buttons in dark mode (and
 * vice versa for tenants who picked pale yellow on light mode).
 */
// Platform fallbacks used when a tenant hasn't picked a brand color
// yet. ALWAYS set the CSS vars (rather than guarding the setProperty
// calls) so converted components can use `bg-[var(--color-primary)]`
// without worrying about the var being unset and the background
// rendering as transparent. The fallback grays are intentionally
// neutral — anything more saturated would look like a tenant choice
// the staff didn't make.
const DEFAULT_PRIMARY = "#4B5563";   // gray-600
const DEFAULT_SECONDARY = "#6B7280"; // gray-500

/**
 * Action colors, retired 2026-10-31 (PM 5b6978ff). A club's identity in the
 * app is its logo plus ONE color (Dan's decision), and that color marks only
 * the selected tab / in-page navigation item, never a button. So the separate
 * Save-button and Export-button colors (settings.save_button_color,
 * settings.export_button_color) and the club font (settings.font_family)
 * are no longer read or applied. The columns stay in the database.
 *
 * The variable names stay because <Button variant="save" | "export"> and a
 * few hand-styled buttons still read them:
 *
 *   --color-save / -hover / -active / -text
 *       The platform action tokens (--cc-action / -hover / -text, defined in
 *       index.css), never the club's color. Save is a primary action.
 *   --color-export / -hover / -active / -text
 *       A fixed neutral gray, never the club's color: Export is a secondary
 *       action under the accent rule.
 */
export const NEUTRAL_ACTION_COLOR = "#4B5563"; // gray-600, white text at 7.6:1

export function applyCSSVariables(settings: Record<string, any> | null): void {
  const root = document.documentElement;

  // Primary: use the tenant color when set, fall back to neutral gray
  // when not. Either way, every derived var is computed and exposed.
  const p = (settings?.primary_color || DEFAULT_PRIMARY).toString();
  root.style.setProperty("--color-primary", p);
  root.style.setProperty("--color-primary-hover", getDarkerColor(p, 0.1));
  root.style.setProperty("--color-primary-active", getDarkerColor(p, 0.18));
  root.style.setProperty("--color-primary-soft", getLighterColor(p, 0.85));
  root.style.setProperty("--color-primary-text", getReadableTextColor(p));
  root.style.setProperty(
    "--color-primary-safe-light",
    getContrastSafeBrandColor(p, { isDark: false }),
  );
  root.style.setProperty(
    "--color-primary-safe-dark",
    getContrastSafeBrandColor(p, { isDark: true }),
  );

  const s = (settings?.secondary_color || DEFAULT_SECONDARY).toString();
  root.style.setProperty("--color-secondary", s);
  root.style.setProperty("--color-secondary-hover", getDarkerColor(s, 0.1));
  root.style.setProperty("--color-secondary-soft", getLighterColor(s, 0.85));
  root.style.setProperty("--color-secondary-text", getReadableTextColor(s));

  // Action colors. ALWAYS set, like the brand vars above: an undefined
  // custom property invalidates the whole declaration that uses it, which
  // would leave an export or save button with no background at all.
  root.style.setProperty("--color-save", "var(--cc-action)");
  root.style.setProperty("--color-save-hover", "var(--cc-action-hover)");
  root.style.setProperty("--color-save-active", "var(--cc-action-hover)");
  root.style.setProperty("--color-save-text", "var(--cc-action-text)");

  const n = NEUTRAL_ACTION_COLOR;
  root.style.setProperty("--color-export", n);
  root.style.setProperty("--color-export-hover", getDarkerColor(n, 0.1));
  root.style.setProperty("--color-export-active", getDarkerColor(n, 0.18));
  root.style.setProperty("--color-export-text", getReadableTextColor(n));

  if (settings?.accent_color) {
    root.style.setProperty("--color-accent", settings.accent_color);
  }
}

/**
 * Document-level branding chrome: the browser tab title, the
 * PWA / mobile address-bar `theme-color` meta tag. Lightweight
 * companion to applyCSSVariables — runs on the same settings load.
 *
 * Why both, not just CSS vars: the tab title and theme-color meta
 * tag aren't CSS — they're document attributes the browser reads
 * directly. So they need their own apply step but live next to the
 * CSS var apply so future tenant-load code only needs to call one
 * pair of functions.
 *
 * The base app name (passed via baseTitle) hangs off the tenant
 * name on the right so a single "Club Central" mental model is
 * preserved across multi-tenant browsing.
 */
export function applyDocumentChrome(
  settings: Record<string, any> | null,
  baseTitle = "Club Central",
): void {
  if (typeof document === "undefined") return;

  // Title — "{Business Name} · Club Central" reads cleanly in the
  // tab bar across most browsers; degrades to just baseTitle if the
  // tenant has no business_name set.
  const name = (settings?.business_name || "").toString().trim();
  // On a club's own website (the /embed/ pages) the tab belongs to the club:
  // just its name, without "· Club Central" (2026-10-10).
  const embedded =
    typeof window !== "undefined" && /\/embed(\/|$)/.test(window.location?.pathname || "");
  document.title = name ? (embedded ? name : `${name} · ${baseTitle}`) : baseTitle;

  // theme-color meta tag — affects mobile Chrome's address bar tint,
  // PWA splash, and the "share" / app-switch chrome on iOS 15+.
  const primary = (settings?.primary_color || "").toString().trim();
  if (primary) {
    let tag = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null;
    if (!tag) {
      tag = document.createElement("meta");
      tag.name = "theme-color";
      document.head.appendChild(tag);
    }
    tag.content = primary;
  }
}
